/**
 * Tweaks for Shopify Search & Discovery (native) collection filters only.
 * Must not run when the VistaLister custom filter is active
 * (html.vl-collection-filter / assets/collection-filter-large.js).
 *
 * Eyewear Type is radio navigation on All only. Type alone (or type + brands /
 * multi department) stays on /collections/all with filter.p.product_type
 * (Eyeglasses|Sunglasses|Readers) — hub handles are not filter destinations.
 * One department with type → type+department (eyeglasses-mens, …).
 * Department without type stays on all with filter.p.m.custom.departments.
 * Brand-only without type → brand collection. Same matrix as
 * collection-filter-large.js.
 * Theme AJAX skips full-page links via data-not-a-filter (see assets/facets.js).
 *
 * Predictive (N) counts on Type radios come from VistaLister products.json.
 * Department/Brand use checkbox-style counts from the same catalog.
 *
 * Type / Department already in the collection path are not chipped and the
 * Department facet block is hidden (same idea as Type outside All). Brand from
 * the handle can still be a removable chip. Refine filter.* present in the URL
 * but missing from Liquid active_values (common at 0 results) are synthesized
 * as chips from the query string. Brand lists are always built from
 * products.json — S&D filter.values are capped at 50 and must not be used for Brand.
 * The leading count is the real Shopify collection.products_count ("N results"),
 * same total as the load-more bar — not the JSON prediction on Type radios.
 *
 * Theme facets.js injects filter HTML into [data-replace-filter] via AJAX
 * after first paint — re-apply on facets:loaded / MutationObserver.
 *
 * Price range: the theme Apply button is hidden; the selected min/max is
 * applied automatically 2s after the shopper stops moving the handles
 * (debounce), by clicking the hidden theme button so facets.js AJAX runs.
 *
 * The same FacetFiltersForm HTML can exist in several hosts at once:
 * #t4s-filter-hidden (Filter drawer), aside[data-sidebar-content] (desktop
 * column), and #drawer-* Sidebar (mobile / enable_drawer). Enhance every
 * copy — querySelector alone left the Sidebar drawer on raw S&D checkboxes.
 */
(function () {
  "use strict";

  var CUSTOM_FILTER_HTML_CLASS = "vl-collection-filter";
  var STYLE_ID = "collection-filter-native-css";
  var NAV_READY_ATTR = "data-native-nav-ready";
  var NAV_KIND_ATTR = "data-native-nav-kind";
  var PRODUCTS_JSON_URL =
    "https://vistalister.eleganzo.com/data/shopify/retail/products.json";

  var TYPE_OPTIONS = ["eyeglasses", "sunglasses", "readers"];
  var DEPARTMENT_OPTIONS = ["mens", "womens", "girls", "boys"];

  var TYPE_CODE_BY_KEY = { eyeglasses: 1, sunglasses: 2, readers: 3 };
  var DEPARTMENT_CODE_BY_KEY = { mens: 1, womens: 2, girls: 3, boys: 4 };

  var TYPE_HANDLES = {
    eyeglasses: "eyeglasses",
    sunglasses: "sunglasses",
    readers: "readers",
  };

  var TYPE_DEPARTMENT_HANDLES = {
    "eyeglasses-mens": "eyeglasses-mens",
    "eyeglasses-womens": "eyeglasses-womens",
    "eyeglasses-girls": "eyeglasses-girls",
    "eyeglasses-boys": "eyeglasses-boys",
    "sunglasses-mens": "sunglasses-mens",
    "sunglasses-womens": "sunglasses-womens",
    "sunglasses-girls": "sunglasses-girls",
    "sunglasses-boys": "sunglasses-boys",
    "readers-mens": "readers-mens",
    "readers-womens": "readers-womens",
    "readers-girls": "readers-girls",
    "readers-boys": "readers-boys",
  };

  var DEPARTMENT_ONLY_HANDLES = {
    mens: "mens",
    womens: "womens",
    girls: "girls",
    boys: "boys",
  };

  var PARAM_PRODUCT_TYPE = "filter.p.product_type";
  var PARAM_DEPARTMENT = "filter.p.m.custom.departments";
  var PARAM_BRAND = "filter.p.m.custom.brand";
  var PARAM_PRICE_GTE = "filter.v.price.gte";
  var PARAM_PRICE_LTE = "filter.v.price.lte";
  var PRICE_AUTO_ATTR = "data-native-price-auto";
  var PRICE_APPLY_DELAY_MS = 2000;
  var priceApplyTimer = null;

  /* Drop facet params that do not apply after switching eyewear type. */
  var TYPE_IRRELEVANT_PARAMS = {
    eyeglasses: [
      "filter.v.option.strength",
      "filter.p.m.custom.lens_type",
      "filter.v.m.custom.lens_color_pallet",
      "filter.v.m.custom.lens_color",
    ],
    sunglasses: [
      "filter.v.option.strength",
      "filter.v.m.custom.lens_height",
      "filter.p.m.custom.sunglass_clip_ons",
    ],
    readers: [
      "filter.p.m.custom.sunglass_clip_ons",
      "filter.v.m.custom.lens_height",
      "filter.p.m.custom.lens_type",
      "filter.v.m.custom.lens_color_pallet",
      "filter.v.m.custom.lens_color",
    ],
  };

  /* Dict labels aligned with collection-filter-large.js / products.json codes. */
  var CATALOG_DICT = {
    sh: {
      3: "cat eye",
      5: "oval",
      6: "oversized",
      7: "rectangular",
      8: "round",
      10: "square",
      12: "wrap",
      15: "browline",
      16: "geometric",
      17: "aviator",
    },
    fm: {
      1: "metal",
      2: "metal & plastic",
      3: "plastic",
      4: "titanium",
      5: "wood",
      6: "other",
      7: "ultem",
      8: "graphene",
      9: "aluminium",
      10: "carbon fiber",
      11: "ceramic",
    },
    lt: {
      1: "anti-reflective",
      2: "gradient",
      3: "mirrored",
      4: "polarized",
      5: "100% UVA & UVB",
    },
    fc: {
      1: "beige",
      2: "black",
      3: "blue",
      4: "brown",
      5: "crystal",
      6: "gold",
      7: "gray",
      8: "green",
      9: "ivory",
      10: "orange",
      11: "pink",
      12: "purple",
      13: "red",
      14: "silver",
      15: "tortoise",
      16: "white",
      17: "yellow",
      18: "multi-color",
      19: "other",
    },
    lp: {
      1: "beige",
      2: "black",
      3: "blue",
      4: "brown",
      5: "gold",
      6: "gray",
      7: "green",
      8: "ivory",
      9: "orange",
      10: "pink",
      11: "purple",
      12: "red",
      13: "silver",
      14: "white",
      15: "yellow",
      16: "multi-color",
      17: "other",
    },
    sp: { 1: "yes", 2: "no" },
    sc: { 1: "yes", 2: "no" },
    vn: { 1: "yes", 2: "no" },
    cs: { 1: "original", 3: "generic", 4: "no case" },
  };

  /* Refine filters carried into Type/Department predictive counts (URL → product). */
  /* chipPrefix: match Liquid active-filter labels in main-collection.liquid */
  var REFINE_FACETS = [
    { id: "brand", param: PARAM_BRAND, productKey: "b", valueType: "string" },
    { id: "shape", param: "filter.p.m.custom.shape", productKey: "sh", dict: "sh" },
    {
      id: "frame_material",
      param: "filter.p.m.custom.frame_material",
      productKey: "fm",
      dict: "fm",
    },
    {
      id: "frame_color",
      param: "filter.v.m.custom.frame_color_pallet",
      variantKey: "fc",
      dict: "fc",
      chipPrefix: "frame color",
    },
    {
      id: "lens_color",
      param: "filter.v.m.custom.lens_color_pallet",
      variantLensColor: true,
      chipPrefix: "lens color",
    },
    {
      id: "strength",
      param: "filter.v.option.strength",
      variantKey: "st",
      valueType: "string",
      chipPrefix: "strength",
    },
    {
      id: "lens_type",
      param: "filter.p.m.custom.lens_type",
      productKey: "lt",
      dict: "lt",
      chipPrefix: "lens type",
    },
    {
      id: "lens_height",
      param: "filter.v.m.custom.lens_height",
      variantKey: "lh",
      valueType: "number",
      chipPrefix: "lens height",
    },
    {
      id: "spring_hinges",
      param: "filter.p.m.custom.spring_hinges",
      productKey: "sp",
      dict: "sp",
      chipPrefix: "spring hinges",
    },
    {
      id: "clip_ons",
      param: "filter.p.m.custom.sunglass_clip_ons",
      productKey: "sc",
      dict: "sc",
      chipPrefix: "sunglass clip-ons",
    },
    {
      id: "vintage",
      param: "filter.p.m.custom.vintage",
      productKey: "vn",
      dict: "vn",
      chipPrefix: "vintage",
    },
    {
      id: "lens_width",
      param: "filter.v.m.custom.lens_width",
      variantKey: "ew",
      valueType: "number",
      chipPrefix: "lens width",
    },
    {
      id: "bridge_width",
      param: "filter.v.m.custom.bridge_width",
      variantKey: "bw",
      valueType: "number",
      chipPrefix: "bridge width",
    },
    {
      id: "temple_length",
      param: "filter.v.m.custom.temple_length",
      variantKey: "tl",
      valueType: "number",
      chipPrefix: "temple length",
    },
    {
      id: "case",
      param: "filter.p.m.custom.case",
      productKey: "cs",
      dict: "cs",
      chipPrefix: "original case",
    },
  ];

  var catalogState = {
    products: null,
    loadPromise: null,
  };

  var DEPARTMENT_LABEL_ALIASES = {
    mens: "mens",
    men: "mens",
    "men's": "mens",
    womens: "womens",
    women: "womens",
    "women's": "womens",
    girls: "girls",
    girl: "girls",
    boys: "boys",
    boy: "boys",
  };

  var RADIO_SVG =
    '<svg focusable="false" viewBox="0 0 24 24" role="presentation">' +
    '<circle cx="12" cy="12" r="5" fill="currentColor"></circle>' +
    "</svg>";
  var CHECK_SVG =
    '<svg focusable="false" viewBox="0 0 24 24" role="presentation">' +
    '<path fill="currentColor" d="M9 20l-7-7 3-3 4 4L19 4l3 3z"></path>' +
    "</svg>";
  var URL_BRANDS_ATTR = "data-native-url-brands";

  var isEnhancingNav = false;
  var enhanceNavTimer = null;
  var enhanceNavRetryTimer = null;
  var filterHostObserver = null;

  function isCustomFilterActive() {
    return document.documentElement.classList.contains(CUSTOM_FILTER_HTML_CLASS);
  }

  function navToken(kind, context) {
    var countState = "pending";
    if (Array.isArray(catalogState.products)) {
      countState = catalogState.products.length ? "ready" : "empty";
    }
    return [
      kind,
      context.handle || "",
      context.typeKey || "",
      context.departmentKey || "",
      context.brandSlug || "",
      window.location.search || "",
      countState,
    ].join("|");
  }

  /**
   * Shopify-style handle: Latin diacritics → base letters (à → a), then slug.
   * Without NFD, "Face à Face" became face-face instead of face-a-face.
   */
  function handleize(text) {
    return String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/['’]/g, "")
      .replace(/&/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function escapeHtml(text) {
    return String(text || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escapeAttr(text) {
    return escapeHtml(text).replace(/'/g, "&#39;");
  }

  function titleCaseLabel(slug) {
    if (!slug) return "";
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  }

  function labelEquals(a, b) {
    return String(a || "").toLowerCase() === String(b || "").toLowerCase();
  }

  function dictLabel(dictName, code) {
    var map = CATALOG_DICT[dictName];
    if (!map || code === null || code === undefined || code === "") return "";
    return map[code] || "";
  }

  function readRefineFromUrl(dropParams) {
    var params = new URLSearchParams(window.location.search);
    var drop = {};
    (dropParams || []).forEach(function (paramName) {
      drop[paramName] = true;
    });
    var selected = {};
    REFINE_FACETS.forEach(function (facet) {
      if (drop[facet.param]) return;
      var values = params.getAll(facet.param);
      if (!values.length) return;
      selected[facet.id] = values.map(function (value) {
        try {
          return decodeURIComponent(value);
        } catch (error) {
          return value;
        }
      });
    });
    var gteRaw = params.get(PARAM_PRICE_GTE);
    var lteRaw = params.get(PARAM_PRICE_LTE);
    return {
      selected: selected,
      priceGte: gteRaw !== null && gteRaw !== "" ? Number(gteRaw) : null,
      priceLte: lteRaw !== null && lteRaw !== "" ? Number(lteRaw) : null,
    };
  }

  function productInNavCollection(product, context) {
    if (!context) return true;
    var typeCode = context.typeKey ? TYPE_CODE_BY_KEY[context.typeKey] : null;
    var departmentCode = context.departmentKey ? DEPARTMENT_CODE_BY_KEY[context.departmentKey] : null;

    if (context.mode === "all") {
      if (typeCode && product.y !== typeCode) return false;
      return true;
    }

    if (context.mode === "brand" || context.mode === "brand_type") {
      if (context.brandSlug && handleize(product.b || "") !== context.brandSlug) return false;
      if (typeCode && product.y !== typeCode) return false;
      if (departmentCode) {
        var brandDepartments = product.dp || [];
        if (brandDepartments.indexOf(departmentCode) === -1) return false;
      }
      return true;
    }
    if (context.mode === "department") {
      return (product.dp || []).indexOf(departmentCode) !== -1;
    }
    if (context.mode !== "type" && context.mode !== "type_department") return false;
    if (typeCode && product.y !== typeCode) return false;
    if (departmentCode && (product.dp || []).indexOf(departmentCode) === -1) return false;
    return true;
  }

  function productFacetValues(product, facet) {
    var values = [];
    if (facet.productKey) {
      var raw = product[facet.productKey];
      if (facet.valueType === "string") {
        if (raw) values.push(String(raw));
      } else if (facet.dict) {
        var label = dictLabel(facet.dict, raw);
        if (label) values.push(label);
      } else if (raw) {
        values.push(String(raw));
      }
    } else if (facet.variantKey || facet.variantLensColor) {
      (product.vs || []).forEach(function (variant) {
        if (facet.variantLensColor) {
          if (product.y === 2) {
            var lpLabel = dictLabel("lp", variant.lp);
            if (lpLabel) values.push(lpLabel);
            else if (variant.lc) values.push(String(variant.lc));
          } else if (variant.lc) {
            values.push(String(variant.lc));
          }
          return;
        }
        var variantRaw = variant[facet.variantKey];
        if (facet.dict) {
          var variantLabel = dictLabel(facet.dict, variantRaw);
          if (variantLabel) values.push(variantLabel);
        } else if (variantRaw) {
          values.push(String(variantRaw));
        }
      });
    }
    return values;
  }

  function variantMatchesRefine(variant, product, selected) {
    for (var i = 0; i < REFINE_FACETS.length; i++) {
      var facet = REFINE_FACETS[i];
      if (!facet.variantKey && !facet.variantLensColor) continue;
      var wanted = selected[facet.id];
      if (!wanted || !wanted.length) continue;
      var variantValues = [];
      if (facet.variantLensColor) {
        if (product.y === 2) {
          var lp = dictLabel("lp", variant.lp);
          if (lp) variantValues.push(lp);
          else if (variant.lc) variantValues.push(String(variant.lc));
        } else if (variant.lc) {
          variantValues.push(String(variant.lc));
        }
      } else if (facet.dict) {
        var lbl = dictLabel(facet.dict, variant[facet.variantKey]);
        if (lbl) variantValues.push(lbl);
      } else if (variant[facet.variantKey]) {
        variantValues.push(String(variant[facet.variantKey]));
      }
      var hit = wanted.some(function (value) {
        return variantValues.some(function (actual) {
          return labelEquals(value, actual);
        });
      });
      if (!hit) return false;
    }
    return true;
  }

  function productMatchesRefine(product, selected, priceGte, priceLte) {
    for (var i = 0; i < REFINE_FACETS.length; i++) {
      var facet = REFINE_FACETS[i];
      if (!facet.productKey) continue;
      var wanted = selected[facet.id];
      if (!wanted || !wanted.length) continue;
      var productValues = productFacetValues(product, facet);
      var productHit = wanted.some(function (value) {
        return productValues.some(function (actual) {
          return labelEquals(value, actual);
        });
      });
      if (!productHit) return false;
    }

    var hasVariantFacet = REFINE_FACETS.some(function (facet) {
      return (
        (facet.variantKey || facet.variantLensColor) &&
        selected[facet.id] &&
        selected[facet.id].length
      );
    });
    if (hasVariantFacet) {
      var anyVariant = (product.vs || []).some(function (variant) {
        return variantMatchesRefine(variant, product, selected);
      });
      if (!anyVariant) return false;
    }

    var price = Number(product.pr) || 0;
    if (priceGte !== null && !isNaN(priceGte) && price < priceGte) return false;
    if (priceLte !== null && !isNaN(priceLte) && price > priceLte) return false;
    return true;
  }

  function destinationContextForType(typeKey, context) {
    return resolveDestination(
      typeKey,
      effectiveDepartmentKeys(context),
      effectiveBrandLabels(context)
    );
  }

  function countForNavOption(kind, optionKey, context) {
    if (kind !== "type") return null;
    if (!Array.isArray(catalogState.products) || !catalogState.products.length) {
      return null;
    }
    var departments = effectiveDepartmentKeys(context);
    var brands = effectiveBrandLabels(context);
    var destination = resolveDestination(optionKey, departments, brands);
    var refine = readRefineFromUrl(dropParamsForType(optionKey).concat([PARAM_DEPARTMENT, PARAM_BRAND]));
    var queryDepartments = departmentsForQuery(destination, departments).map(normalizeDepartmentKey);
    var queryBrands = brandsForQuery(destination, brands);
    if (queryBrands.length) refine.selected.brand = queryBrands;

    var count = 0;
    for (var i = 0; i < catalogState.products.length; i++) {
      var product = catalogState.products[i];
      if (!productInNavCollection(product, destination)) continue;
      if (queryDepartments.length) {
        var departmentCodes = queryDepartments
          .map(function (key) {
            return DEPARTMENT_CODE_BY_KEY[key];
          })
          .filter(Boolean);
        var productDepartments = product.dp || [];
        var departmentHit = departmentCodes.some(function (code) {
          return productDepartments.indexOf(code) !== -1;
        });
        if (!departmentHit) continue;
      }
      if (!productMatchesRefine(product, refine.selected, refine.priceGte, refine.priceLte)) {
        continue;
      }
      count += 1;
    }
    return count;
  }

  function ensureCatalogLoaded() {
    if (Array.isArray(catalogState.products)) {
      return Promise.resolve(catalogState.products);
    }
    if (catalogState.loadPromise) return catalogState.loadPromise;
    catalogState.loadPromise = fetch(PRODUCTS_JSON_URL + "?v=" + Date.now(), {
      credentials: "omit",
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Failed to load products.json");
        return response.json();
      })
      .then(function (payload) {
        catalogState.products = payload.p || [];
        return catalogState.products;
      })
      .catch(function (error) {
        console.warn("[collection-filter-native] catalog counts unavailable", error);
        catalogState.products = [];
        return catalogState.products;
      });
    return catalogState.loadPromise;
  }

  function normalizeDepartmentKey(label) {
    var key = handleize(label).replace(/-/g, "");
    if (DEPARTMENT_LABEL_ALIASES[key]) return DEPARTMENT_LABEL_ALIASES[key];
    var dashed = handleize(label);
    if (DEPARTMENT_LABEL_ALIASES[dashed]) return DEPARTMENT_LABEL_ALIASES[dashed];
    return dashed;
  }

  function normalizeTypeKey(label) {
    var key = handleize(label);
    if (TYPE_HANDLES[key]) return key;
    return key;
  }

  /** Shopify product.type for filter.p.product_type (Eyeglasses, …). */
  function productTypeParamValue(typeKey) {
    if (!typeKey || !TYPE_HANDLES[typeKey]) return "";
    return titleCaseLabel(typeKey);
  }

  function typeKeyFromProductTypeParam(value) {
    if (!value) return null;
    var key = normalizeTypeKey(value);
    return TYPE_HANDLES[key] ? key : null;
  }

  function applyProductTypeFromUrl(context) {
    if (!context || context.mode !== "all") return context;
    var params = new URLSearchParams(window.location.search);
    context.typeKey = typeKeyFromProductTypeParam(params.get(PARAM_PRODUCT_TYPE));
    return context;
  }

  function collectionHandleFromPath() {
    var match = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : "";
  }

  function parseCollectionContext(handle) {
    handle = (handle || "").toLowerCase();
    var context = {
      handle: handle,
      mode: "other",
      typeKey: null,
      departmentKey: null,
      brandSlug: "",
    };

    if (!handle || handle === "all") {
      context.mode = "all";
      return applyProductTypeFromUrl(context);
    }

    if (TYPE_HANDLES[handle]) {
      context.mode = "type";
      context.typeKey = handle;
      return context;
    }

    if (TYPE_DEPARTMENT_HANDLES[handle]) {
      context.mode = "type_department";
      var typeDepartmentParts = handle.split("-");
      context.typeKey = typeDepartmentParts[0];
      context.departmentKey = typeDepartmentParts[1];
      return context;
    }

    if (DEPARTMENT_ONLY_HANDLES[handle]) {
      context.mode = "department";
      context.departmentKey = handle;
      return context;
    }

    var parts = handle.split("-");
    var typeKey = null;
    var departmentKey = null;
    var brandParts = [];

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (part === "eyeglasses" || part === "sunglasses" || part === "readers") {
        typeKey = part;
        continue;
      }
      if (part === "mens" || part === "womens" || part === "girls" || part === "boys") {
        departmentKey = part;
        continue;
      }
      brandParts.push(part);
    }

    context.typeKey = typeKey;
    context.departmentKey = departmentKey;
    context.brandSlug = brandParts.join("-");

    if (typeKey && departmentKey) context.mode = "type_department";
    else if (typeKey && context.brandSlug) context.mode = "brand_type";
    else if (typeKey) context.mode = "type";
    else if (departmentKey) context.mode = "department";
    else if (context.brandSlug) context.mode = "brand";

    return context;
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent =
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-facet[" +
      NAV_KIND_ATTR +
      "] .t4s-filter__values{" +
      "list-style:none;" +
      "}" +
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-radio-wrapper{" +
      "width:16px;height:16px;min-width:16px;border:1px solid var(--border-color);" +
      "border-radius:50%;display:inline-flex;align-items:center;justify-content:center;" +
      "margin-inline-end:10px;vertical-align:middle;box-sizing:border-box;" +
      "}" +
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-radio-wrapper svg{" +
      "width:8px;height:8px;pointer-events:none;transform:scale(0);" +
      "transition:.25s ease-in-out;color:var(--t4s-light-color);" +
      "}" +
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-filter__values .is--selected>a>.t4s-radio-wrapper{" +
      "background-color:var(--accent-color);border-color:var(--accent-color);" +
      "}" +
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-filter__values .is--selected>a>.t4s-radio-wrapper svg{" +
      "transform:scale(1);" +
      "}" +
      "html:not(." +
      CUSTOM_FILTER_HTML_CLASS +
      ") .t4s-price_slider_amount .t4s-price_slider_btn{" +
      "display:none!important;" +
      "}";
    document.head.appendChild(style);
  }

  function buildCarriedSearch(options) {
    options = options || {};
    var dropKeys = {};
    var dropList = options.dropParams || [];
    for (var i = 0; i < dropList.length; i++) {
      dropKeys[dropList[i]] = true;
    }

    var params = new URLSearchParams(window.location.search);
    var kept = new URLSearchParams();
    params.forEach(function (value, key) {
      if (dropKeys[key]) return;
      kept.append(key, value);
    });

    if (options.setParams) {
      Object.keys(options.setParams).forEach(function (key) {
        kept.delete(key);
        var value = options.setParams[key];
        if (value !== null && value !== undefined && value !== "") {
          kept.set(key, value);
        }
      });
    }

    if (options.appendParams) {
      Object.keys(options.appendParams).forEach(function (key) {
        kept.delete(key);
        var values = options.appendParams[key];
        if (!Array.isArray(values)) values = values ? [values] : [];
        values.forEach(function (value) {
          if (value !== null && value !== undefined && value !== "") {
            kept.append(key, value);
          }
        });
      });
    }

    var query = kept.toString();
    return query ? "?" + query : "";
  }

  function uniqueDepartmentKeys(keys) {
    var seen = {};
    var result = [];
    (keys || []).forEach(function (key) {
      var normalized = normalizeDepartmentKey(key);
      if (!normalized || !DEPARTMENT_ONLY_HANDLES[normalized] || seen[normalized]) return;
      seen[normalized] = true;
      result.push(normalized);
    });
    return result;
  }

  function uniqueBrandLabelsList(labels) {
    var seen = {};
    var result = [];
    (labels || []).forEach(function (label) {
      if (!label) return;
      var key = String(label).toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      result.push(String(label));
    });
    return result;
  }

  function selectedDepartmentKeysFromUrl() {
    return uniqueDepartmentKeys(
      new URLSearchParams(window.location.search).getAll(PARAM_DEPARTMENT).map(decodeParamValue)
    );
  }

  function effectiveDepartmentKeys(context) {
    var keys = [];
    if (context && context.departmentKey) keys.push(context.departmentKey);
    return uniqueDepartmentKeys(keys.concat(selectedDepartmentKeysFromUrl()));
  }

  function effectiveBrandLabels(context) {
    var labels = [];
    if (context && context.brandSlug) {
      var contextBrand = brandLabelFromSlug(context.brandSlug);
      if (contextBrand) labels.push(contextBrand);
    }
    return uniqueBrandLabelsList(labels.concat(selectedBrandLabelsFromUrl()));
  }

  /**
   * Pick collection handle from type / department / brand (MF matrix).
   * Exactly 1 department with type → type+department;
   * department without type → all + filter.p.m.custom.departments
   * (department-only handles like mens are not used);
   * else with type → all + filter.p.product_type (not type hubs);
   * exactly 1 brand without type → brand collection.
   */
  function resolveDestination(typeKey, departmentKeys, brandLabels) {
    var departments = uniqueDepartmentKeys(departmentKeys);
    var brands = uniqueBrandLabelsList(brandLabels);

    if (departments.length === 1 && typeKey) {
      var departmentKey = departments[0];
      var typeDepartmentHandle = String(typeKey) + "-" + String(departmentKey);
      if (TYPE_DEPARTMENT_HANDLES[typeDepartmentHandle]) {
        return {
          handle: TYPE_DEPARTMENT_HANDLES[typeDepartmentHandle],
          mode: "type_department",
          typeKey: typeKey,
          departmentKey: departmentKey,
          brandSlug: "",
        };
      }
    }

    if (typeKey) {
      return {
        handle: "all",
        mode: "all",
        typeKey: typeKey,
        departmentKey: null,
        brandSlug: "",
      };
    }

    if (brands.length === 1) {
      var brandSlug = handleize(brands[0]);
      return {
        handle: brandSlug,
        mode: "brand",
        typeKey: null,
        departmentKey: null,
        brandSlug: brandSlug,
      };
    }

    return {
      handle: "all",
      mode: "all",
      typeKey: null,
      departmentKey: null,
      brandSlug: "",
    };
  }

  function departmentsForQuery(dest, departmentKeys) {
    var keys = uniqueDepartmentKeys(departmentKeys);
    if (
      dest &&
      (dest.mode === "type_department" || dest.mode === "department") &&
      dest.departmentKey
    ) {
      return keys.filter(function (key) {
        return key !== dest.departmentKey;
      });
    }
    return keys;
  }

  function brandsForQuery(dest, brandLabels) {
    var brands = uniqueBrandLabelsList(brandLabels);
    if (dest && (dest.mode === "brand" || dest.mode === "brand_type") && dest.brandSlug) {
      return brands.filter(function (label) {
        return handleize(label) !== dest.brandSlug;
      });
    }
    return brands;
  }

  function buildUrlForDestination(dest, departmentKeys, brandLabels, dropParams) {
    var queryDepartments = departmentsForQuery(dest, departmentKeys);
    var queryBrands = brandsForQuery(dest, brandLabels);
    var append = {};
    append[PARAM_DEPARTMENT] = queryDepartments;
    append[PARAM_BRAND] = queryBrands;
    var drop = (dropParams || []).slice();
    if (drop.indexOf(PARAM_DEPARTMENT) === -1) drop.push(PARAM_DEPARTMENT);
    if (drop.indexOf(PARAM_BRAND) === -1) drop.push(PARAM_BRAND);
    if (drop.indexOf(PARAM_PRODUCT_TYPE) === -1) drop.push(PARAM_PRODUCT_TYPE);
    if (dest.handle === "all" && dest.typeKey) {
      var typeParam = productTypeParamValue(dest.typeKey);
      if (typeParam) append[PARAM_PRODUCT_TYPE] = [typeParam];
    }
    return "/collections/" + dest.handle + buildCarriedSearch({
      dropParams: drop,
      appendParams: append,
    });
  }

  function dropParamsForType(typeKey) {
    var list = [PARAM_PRODUCT_TYPE];
    var extra = TYPE_IRRELEVANT_PARAMS[typeKey] || [];
    return list.concat(extra);
  }

  function collectionUrlForType(typeKey, context) {
    var dest = resolveDestination(
      typeKey,
      effectiveDepartmentKeys(context),
      effectiveBrandLabels(context)
    );
    return "/collections/" + dest.handle;
  }

  /**
   * Target after removing one handle dimension, re-resolved via the matrix.
   */
  function destinationAfterRemovingContext(dimension, context) {
    var typeKey = context.typeKey || null;
    var departments = effectiveDepartmentKeys(context);
    var brands = effectiveBrandLabels(context);

    if (dimension === "type") {
      typeKey = null;
    } else if (dimension === "department" && context.departmentKey) {
      departments = departments.filter(function (key) {
        return key !== context.departmentKey;
      });
    } else if (dimension === "brand" && context.brandSlug) {
      brands = brands.filter(function (label) {
        return handleize(label) !== context.brandSlug;
      });
    }

    return resolveDestination(typeKey, departments, brands);
  }

  function collectionUrlAfterRemovingContext(dimension, context) {
    return "/collections/" + destinationAfterRemovingContext(dimension, context).handle;
  }

  function dropParamsForContextRemove(dimension, context) {
    if (dimension === "type") {
      return dropParamsForType(context.typeKey || "eyeglasses");
    }
    if (dimension === "department") return [PARAM_DEPARTMENT];
    if (dimension === "brand") return [PARAM_BRAND];
    return [];
  }

  function brandLabelFromSlug(slug) {
    if (!slug) return "";
    if (Array.isArray(catalogState.products) && catalogState.products.length) {
      for (var i = 0; i < catalogState.products.length; i++) {
        var productBrand = catalogState.products[i].b;
        if (!productBrand) continue;
        if (handleize(productBrand) === slug) return String(productBrand);
      }
    }
    return slug
      .split("-")
      .filter(Boolean)
      .map(function (part) {
        return titleCaseLabel(part);
      })
      .join(" ");
  }

  function contextChipsFor(context) {
    var chips = [];
    if (!context || context.mode === "all" || context.mode === "other") return chips;

    /* Type / Department in the path are collection context — not removable chips. */
    if (context.brandSlug) {
      var brandDest = destinationAfterRemovingContext("brand", context);
      var brandsWithout = effectiveBrandLabels(context).filter(function (label) {
        return handleize(label) !== context.brandSlug;
      });
      chips.push({
        dimension: "brand",
        label: brandLabelFromSlug(context.brandSlug),
        path: "/collections/" + brandDest.handle,
        dropParams: dropParamsForContextRemove("brand", context),
        departments: effectiveDepartmentKeys(context),
        brands: brandsWithout,
        dest: brandDest,
      });
    }
    return chips;
  }

  function findActiveFiltersRow() {
    return (
      document.querySelector(".t4s-main-collection-page > .t4s-active-filters") ||
      document.querySelector(".t4s-main-collection-page .t4s-active-filters")
    );
  }

  /**
   * Real storefront total (Shopify collection.products_count).
   * Prefer the count Liquid rendered into the active-filters row, then the
   * load-more bar ("of N products"). Do not use JSON facet predictions.
   */
  function shopifyResultsCount() {
    var countNode = document.querySelector(
      ".t4s-main-collection-page .t4s-active-filters__count[data-native-results-count]"
    );
    if (countNode) {
      var fromNode = parseInt(countNode.getAttribute("data-native-results-count"), 10);
      if (!isNaN(fromNode)) return fromNode;
    }
    var row = findActiveFiltersRow();
    if (row && row.getAttribute("data-products-count")) {
      var fromRow = parseInt(row.getAttribute("data-products-count"), 10);
      if (!isNaN(fromRow)) return fromRow;
    }
    var bar = document.querySelector(".t4s-main-collection-page .t4s-lm-bar--txt");
    if (bar) {
      var match = (bar.textContent || "").replace(/,/g, "").match(/of\s+(\d+)\s+products/i);
      if (match) {
        var fromBar = parseInt(match[1], 10);
        if (!isNaN(fromBar)) return fromBar;
      }
    }
    return null;
  }

  function ensureResultsCount(row) {
    if (!row) return;
    var count = shopifyResultsCount();
    if (count === null) return;
    var label = count + " results";
    var countNode = row.querySelector(".t4s-active-filters__count");
    if (!countNode) {
      row.insertAdjacentHTML(
        "afterbegin",
        '<div class="t4s-active-filters__count t4s-d-inline-block" data-native-results-count="' +
          count +
          '">' +
          label +
          "</div>"
      );
      return;
    }
    countNode.setAttribute("data-native-results-count", String(count));
    if ((countNode.textContent || "").replace(/\s+/g, " ").trim() !== label) {
      countNode.textContent = label;
    }
  }

  function ensureActiveFiltersRow() {
    var row = findActiveFiltersRow();
    if (row) return row;

    var host = document.querySelector(".t4s-main-collection-page");
    if (!host) return null;

    row = document.createElement("div");
    row.className = "t4s-active-filters";
    row.setAttribute("data-native-context-row", "1");

    var grid =
      host.querySelector("[data-contentlm-replace]") ||
      host.querySelector(".t4s-products") ||
      host.firstElementChild;
    if (grid && grid.parentNode === host) {
      host.insertBefore(row, grid);
    } else {
      host.insertBefore(row, host.firstChild);
    }
    return row;
  }

  function buildContextChipHtml(chip) {
    var href =
      chip.dest
        ? buildUrlForDestination(
            chip.dest,
            chip.departments || [],
            chip.brands || [],
            chip.dropParams || []
          )
        : chip.path + buildCarriedSearch({ dropParams: chip.dropParams || [] });
    return (
      '<a class="t4s-active-filters__remove-filter" href="' +
      escapeAttr(href) +
      '" data-not-a-filter data-native-context-chip="' +
      escapeAttr(chip.dimension) +
      '">' +
      escapeHtml(chip.label) +
      "</a>"
    );
  }

  /** Clear all: same collection path, no query string. */
  function clearAllHref() {
    return window.location.pathname;
  }

  function refineChipLabel(facet, value) {
    var display = String(value || "");
    if (facet.chipPrefix) return facet.chipPrefix + ": " + display;
    return display;
  }

  function decodeParamValue(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function buildSearchRemovingParamValue(paramName, valueToRemove) {
    var params = new URLSearchParams(window.location.search);
    var kept = new URLSearchParams();
    params.forEach(function (value, key) {
      if (key === paramName) {
        var decoded = decodeParamValue(value);
        if (decoded === valueToRemove || value === valueToRemove) return;
      }
      kept.append(key, value);
    });
    var query = kept.toString();
    return query ? "?" + query : "";
  }

  function buildSearchRemovingParams(paramNames) {
    var drop = {};
    (paramNames || []).forEach(function (name) {
      drop[name] = true;
    });
    var params = new URLSearchParams(window.location.search);
    var kept = new URLSearchParams();
    params.forEach(function (value, key) {
      if (drop[key]) return;
      kept.append(key, value);
    });
    var query = kept.toString();
    return query ? "?" + query : "";
  }

  function urlHasRefineFilters() {
    var refine = readRefineFromUrl([]);
    if (Object.keys(refine.selected).length) return true;
    return refine.priceGte !== null || refine.priceLte !== null;
  }

  function rowAlreadyHasRefineChip(row, label, param, value) {
    if (!row) return false;
    var links = row.querySelectorAll(
      "a.t4s-active-filters__remove-filter:not([data-native-context-chip])"
    );
    var wanted = String(label || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (
        link.getAttribute("data-native-refine-param") === param &&
        link.getAttribute("data-native-refine-value") === value
      ) {
        return true;
      }
      var text = (link.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (text === wanted) return true;
    }
    return false;
  }

  function buildRefineChipHtml(label, href, param, value) {
    return (
      '<a class="t4s-active-filters__remove-filter" href="' +
      escapeAttr(href) +
      '" data-not-a-filter data-native-refine-chip="1" data-native-refine-param="' +
      escapeAttr(param) +
      '" data-native-refine-value="' +
      escapeAttr(value) +
      '">' +
      escapeHtml(label) +
      "</a>"
    );
  }

  /**
   * S&D often omits active_values (and thus Liquid chips) when a carried
   * refine filter yields 0 products. Fill those chips from the URL.
   */
  function ensureRefineChipsFromUrl(row) {
    if (!row) return 0;

    var searchKey = window.location.search || "";
    /*
     * Idempotent: tearing down and re-inserting chips on every enhance pass
     * retriggers MutationObserver → infinite shake and dead clicks (e.g. Ad.lib).
     */
    if (row.getAttribute("data-native-refine-ready") === searchKey) {
      return row.querySelectorAll("[data-native-refine-chip]").length;
    }

    row.querySelectorAll("[data-native-refine-chip]").forEach(function (node) {
      node.remove();
    });

    var refine = readRefineFromUrl([]);
    var path = window.location.pathname;
    var htmlParts = [];
    var injected = 0;

    REFINE_FACETS.forEach(function (facet) {
      var values = refine.selected[facet.id] || [];
      values.forEach(function (value) {
        var label = refineChipLabel(facet, value);
        if (rowAlreadyHasRefineChip(row, label, facet.param, value)) return;
        var href = path + buildSearchRemovingParamValue(facet.param, value);
        htmlParts.push(buildRefineChipHtml(label, href, facet.param, value));
        injected += 1;
      });
    });

    if (refine.priceGte !== null || refine.priceLte !== null) {
      var hasPriceChip = false;
      row
        .querySelectorAll(
          "a.t4s-active-filters__remove-filter:not([data-native-context-chip])"
        )
        .forEach(function (link) {
          if (link.getAttribute("data-native-refine-param") === PARAM_PRICE_GTE) {
            hasPriceChip = true;
            return;
          }
          var text = (link.textContent || "").replace(/\s+/g, " ").trim();
          if (/[\$€£]/.test(text) && text.indexOf("-") !== -1) hasPriceChip = true;
        });
      if (!hasPriceChip) {
        var priceLabelParts = [];
        if (refine.priceGte !== null) priceLabelParts.push(String(refine.priceGte));
        if (refine.priceLte !== null) priceLabelParts.push(String(refine.priceLte));
        var priceLabel = priceLabelParts.join(" - ");
        var priceHref =
          path + buildSearchRemovingParams([PARAM_PRICE_GTE, PARAM_PRICE_LTE]);
        htmlParts.push(
          buildRefineChipHtml(priceLabel, priceHref, PARAM_PRICE_GTE, priceLabel)
        );
        injected += 1;
      }
    }

    if (htmlParts.length) {
      var clearLink = row.querySelector("a.t4s-active-filters__clear");
      if (clearLink) {
        clearLink.insertAdjacentHTML("beforebegin", htmlParts.join(""));
      } else {
        row.insertAdjacentHTML("beforeend", htmlParts.join(""));
      }
    }

    row.setAttribute("data-native-refine-ready", searchKey);
    return injected;
  }

  function syncActiveFiltersClearLink(row, contextChipCount) {
    if (!row) return;
    var refineChips = row.querySelectorAll(
      "a.t4s-active-filters__remove-filter:not([data-native-context-chip])"
    );
    var totalChips = contextChipCount + refineChips.length;
    var clearLink = row.querySelector("a.t4s-active-filters__clear");

    if (totalChips > 1) {
      var clearHref = clearAllHref();
      if (clearLink) {
        clearLink.setAttribute("href", clearHref);
        clearLink.setAttribute("data-not-a-filter", "");
        clearLink.setAttribute("data-native-clear-context", "1");
      } else {
        row.insertAdjacentHTML(
          "beforeend",
          '<a href="' +
            escapeAttr(clearHref) +
            '" class="t4s-active-filters__clear" data-not-a-filter data-native-clear-context="1">Clear all</a>'
        );
      }
    } else if (clearLink && clearLink.getAttribute("data-native-clear-context") === "1") {
      clearLink.remove();
    }
  }

  /**
   * Inject department/brand context chips into .t4s-active-filters (not type),
   * and synthesize refine chips from URL when S&D/Liquid omitted them.
   * Theme AJAX replaces this row — call again after facets:loaded.
   */
  function enhanceActiveFilterContextChips() {
    if (isCustomFilterActive()) return;

    var context = parseCollectionContext(collectionHandleFromPath());
    var chips = contextChipsFor(context);
    var row = findActiveFiltersRow();
    var token = navToken("active-context", context);
    var searchKey = window.location.search || "";
    var hasRefine = urlHasRefineFilters();

    if (!chips.length) {
      row = findActiveFiltersRow();
      if (!row && (shopifyResultsCount() !== null || hasRefine)) {
        row = ensureActiveFiltersRow();
      }
      /*
       * type+department paths have no context chips. Do not tear down Clear all
       * / refine chips on every MutationObserver pass — that reflow loop makes
       * labels like Ad.lib shake and swallows clicks.
       */
      if (
        row &&
        row.getAttribute("data-native-refine-ready") === searchKey &&
        !row.querySelector("[data-native-context-chip]")
      ) {
        ensureResultsCount(row);
        syncActiveFiltersClearLink(row, 0);
        return;
      }

      document.querySelectorAll("[data-native-context-chip]").forEach(function (node) {
        node.remove();
      });
      document.querySelectorAll("[data-native-clear-context]").forEach(function (node) {
        node.remove();
      });
      if (row && row.getAttribute("data-native-context-row") === "1") {
        var leftover = row.querySelectorAll(
          "a.t4s-active-filters__remove-filter, a.t4s-active-filters__clear"
        );
        if (!leftover.length && shopifyResultsCount() === null && !hasRefine) row.remove();
      }
      row = findActiveFiltersRow();
      if (!row && (shopifyResultsCount() !== null || hasRefine)) {
        row = ensureActiveFiltersRow();
      }
      ensureResultsCount(row);
      if (row) row.removeAttribute("data-native-context-ready");
      ensureRefineChipsFromUrl(row);
      syncActiveFiltersClearLink(row, 0);
      return;
    }

    if (
      row &&
      row.getAttribute("data-native-context-ready") === token &&
      row.getAttribute("data-native-refine-ready") === searchKey &&
      row.querySelectorAll("[data-native-context-chip]").length === chips.length
    ) {
      ensureResultsCount(row);
      return;
    }

    document.querySelectorAll("[data-native-context-chip]").forEach(function (node) {
      node.remove();
    });
    document.querySelectorAll("[data-native-clear-context]").forEach(function (node) {
      node.remove();
    });

    row = ensureActiveFiltersRow();
    if (!row) return;

    ensureResultsCount(row);
    row.setAttribute("data-native-context-ready", token);

    var chipHtml = chips.map(buildContextChipHtml).join("");
    var countNode = row.querySelector(".t4s-active-filters__count");
    if (countNode) {
      countNode.insertAdjacentHTML("afterend", chipHtml);
    } else {
      row.insertAdjacentHTML("afterbegin", chipHtml);
    }

    ensureRefineChipsFromUrl(row);
    syncActiveFiltersClearLink(row, chips.length);
  }

  function bindContextChipNavigation() {
    if (document.documentElement.getAttribute("data-native-context-nav-bound") === "1") {
      return;
    }
    document.documentElement.setAttribute("data-native-context-nav-bound", "1");

    /* Capture: beat theme facets.js AJAX on .t4s-active-filters a */
    document.addEventListener(
      "click",
      function (event) {
        if (isCustomFilterActive()) return;
        var link = event.target.closest(
          "a[data-native-context-chip], a[data-native-clear-context], a[data-native-refine-chip]"
        );
        if (!link) return;
        var href = link.getAttribute("href");
        if (!href || href === "#") return;
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        window.location.assign(href);
      },
      true
    );
  }

  function isNativeFacetHost(node) {
    return !!(node && !node.closest("[data-vl-collection-filter]"));
  }

  /**
   * All native Type / Department / Brand facet blocks (every drawer + sidebar copy).
   * Dedupes by element identity when class + title selectors both match.
   */
  function findFacetBlocks(kind) {
    var byClass = {
      type: ".t4s-facet.is--blockideyewear-type:not([data-vl-facet-block])",
      department: ".t4s-facet.is--blockiddepartment:not([data-vl-facet-block]), .t4s-facet.is--blockidgender:not([data-vl-facet-block])",
      brand: ".t4s-facet.is--blockidbrand:not([data-vl-facet-block])",
    };
    var wanted = {
      type: "eyewear type",
      department: "department",
      brand: "brand",
    }[kind];
    var seen = [];
    var result = [];

    function pushUnique(block) {
      if (!isNativeFacetHost(block)) return;
      if (seen.indexOf(block) !== -1) return;
      seen.push(block);
      result.push(block);
    }

    var selector = byClass[kind];
    if (selector) {
      var byClassNodes = document.querySelectorAll(selector);
      for (var c = 0; c < byClassNodes.length; c++) {
        pushUnique(byClassNodes[c]);
      }
    }

    var titles = document.querySelectorAll(
      ".t4s-facet:not([data-vl-facet-block]) > .t4s-facet-title"
    );
    for (var i = 0; i < titles.length; i++) {
      var label = (titles[i].textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (label === wanted) {
        pushUnique(titles[i].parentElement);
      }
    }
    return result;
  }

  function findFacetBlock(kind) {
    var blocks = findFacetBlocks(kind);
    return blocks.length ? blocks[0] : null;
  }

  function findNativeFilterForms() {
    var forms = document.querySelectorAll(
      "#FacetFiltersForm:not([data-vl-collection-filter]), [data-replace-filter] #FacetFiltersForm:not([data-vl-collection-filter])"
    );
    var seen = [];
    var result = [];
    for (var i = 0; i < forms.length; i++) {
      if (seen.indexOf(forms[i]) !== -1) continue;
      seen.push(forms[i]);
      result.push(forms[i]);
    }
    return result;
  }

  function getOrCreateList(block) {
    var content = block.querySelector(".t4s-facet-content");
    if (!content) {
      content = document.createElement("div");
      content.className = "t4s-facet-content";
      block.appendChild(content);
    }
    var list = content.querySelector("ul.t4s-filter__values");
    if (!list) {
      list = document.createElement("ul");
      list.className = "t4s-filter__values is--style-checkbox t4s-current-scrollbar";
      content.appendChild(list);
    }
    return list;
  }

  function readExistingLabels(list) {
    var labels = [];
    var items = list.querySelectorAll("li > a");
    for (var i = 0; i < items.length; i++) {
      var clone = items[i].cloneNode(true);
      var extras = clone.querySelectorAll(
        ".t4s-checkbox-wrapper, .t4s-radio-wrapper, .t4s-value-count, .t4s-shape-icon"
      );
      for (var j = 0; j < extras.length; j++) {
        extras[j].remove();
      }
      var text = (clone.textContent || "").replace(/\s+/g, " ").trim();
      if (text) labels.push(text);
    }
    return labels;
  }

  function buildNavItemHtml(label, href, isSelected, dataAttrs, count) {
    var hasCount = count !== null && count !== undefined;
    var isDisabled = !isSelected && hasCount && Number(count) === 0;
    var liClasses = [];
    if (isSelected) liClasses.push("is--selected");
    if (isDisabled) liClasses.push("is--disabled");
    var liClass = liClasses.length ? ' class="' + liClasses.join(" ") + '"' : "";
    var attrs = dataAttrs || "";
    var countHtml = hasCount ? '<span class="t4s-value-count">(' + count + ")</span>" : "";
    return (
      "<li" +
      liClass +
      attrs +
      '><a href="' +
      escapeAttr(href) +
      '" data-not-a-filter>' +
      '<div class="t4s-radio-wrapper t4s-pr t4s-oh">' +
      RADIO_SVG +
      "</div>" +
      escapeHtml(label) +
      countHtml +
      "</a></li>"
    );
  }

  function buildCheckboxItemHtml(label, href, isSelected, count, dataAttrs, useNotAFilter) {
    var isDisabled = !isSelected && Number(count) === 0;
    var liClasses = [];
    if (isSelected) liClasses.push("is--selected");
    if (isDisabled) liClasses.push("is--disabled");
    var liClass = liClasses.length ? ' class="' + liClasses.join(" ") + '"' : "";
    var attrs = dataAttrs || "";
    var countHtml =
      count === null || count === undefined
        ? ""
        : '<span class="t4s-value-count">(' + count + ")</span>";
    var notAFilter = useNotAFilter ? " data-not-a-filter" : "";
    return (
      "<li" +
      liClass +
      attrs +
      '><a href="' +
      escapeAttr(href) +
      '"' +
      notAFilter +
      ">" +
      '<div class="t4s-checkbox-wrapper t4s-pr t4s-oh">' +
      CHECK_SVG +
      "</div>" +
      escapeHtml(label) +
      countHtml +
      "</a></li>"
    );
  }

  function enhanceTypeBlock(block, context) {
    var token = navToken("type", context);
    var list = getOrCreateList(block);
    var alreadyReady = block.getAttribute(NAV_READY_ATTR) === token;
    var hasCounts = !!list.querySelector(".t4s-value-count");
    var countsReady =
      !Array.isArray(catalogState.products) ||
      !catalogState.products.length ||
      hasCounts;
    var hasFullRadioList =
      alreadyReady &&
      list.querySelectorAll("li").length >= TYPE_OPTIONS.length &&
      list.querySelector(".t4s-radio-wrapper") &&
      list.classList.contains("is--style-radio") &&
      !list.classList.contains("is--style-checkbox") &&
      countsReady;
    if (hasFullRadioList) return;

    var existing = readExistingLabels(list);
    var labelsByKey = {};
    for (var i = 0; i < existing.length; i++) {
      var key = normalizeTypeKey(existing[i]);
      if (TYPE_HANDLES[key]) labelsByKey[key] = existing[i];
    }

    var departments = effectiveDepartmentKeys(context);
    var brands = effectiveBrandLabels(context);
    var html = "";
    for (var t = 0; t < TYPE_OPTIONS.length; t++) {
      var typeKey = TYPE_OPTIONS[t];
      var label = labelsByKey[typeKey] || titleCaseLabel(typeKey);
      var dest = resolveDestination(typeKey, departments, brands);
      var href = buildUrlForDestination(dest, departments, brands, dropParamsForType(typeKey));
      var selected = context.typeKey === typeKey;
      var count = countForNavOption("type", typeKey, context);
      html += buildNavItemHtml(
        label,
        href,
        selected,
        ' data-type="' + escapeAttr(typeKey) + '"',
        count
      );
    }

    list.innerHTML = html;
    list.classList.remove("is--style-checkbox");
    list.classList.add("is--style-radio");
    block.setAttribute(NAV_KIND_ATTR, "type");
    block.setAttribute(NAV_READY_ATTR, token);
  }

  function countForDepartmentCheckbox(departmentKey, context) {
    if (!Array.isArray(catalogState.products) || !catalogState.products.length) {
      return null;
    }
    var departmentCode = DEPARTMENT_CODE_BY_KEY[departmentKey];
    if (!departmentCode) return 0;
    var brands = effectiveBrandLabels(context);
    var refine = readRefineFromUrl([PARAM_DEPARTMENT, PARAM_BRAND]);
    if (brands.length) refine.selected.brand = brands;
    var count = 0;
    for (var i = 0; i < catalogState.products.length; i++) {
      var product = catalogState.products[i];
      if (context.typeKey && product.y !== TYPE_CODE_BY_KEY[context.typeKey]) continue;
      var productDepartments = product.dp || [];
      if (productDepartments.indexOf(departmentCode) === -1) continue;
      if (!productMatchesRefine(product, refine.selected, refine.priceGte, refine.priceLte)) {
        continue;
      }
      count += 1;
    }
    return count;
  }

  function enhanceDepartmentBlock(block, context) {
    var token = navToken("department-checkbox", context);
    var list = getOrCreateList(block);
    var alreadyReady = block.getAttribute(NAV_READY_ATTR) === token;
    var hasCounts = !!list.querySelector(".t4s-value-count");
    var countsReady =
      !Array.isArray(catalogState.products) ||
      !catalogState.products.length ||
      hasCounts;
    var hasFullCheckboxList =
      alreadyReady &&
      list.querySelectorAll("li").length >= DEPARTMENT_OPTIONS.length &&
      list.querySelector(".t4s-checkbox-wrapper") &&
      list.classList.contains("is--style-checkbox") &&
      !list.classList.contains("is--style-radio") &&
      countsReady;
    if (hasFullCheckboxList) return;

    var existing = readExistingLabels(list);
    var labelsByKey = {};
    for (var i = 0; i < existing.length; i++) {
      var key = normalizeDepartmentKey(existing[i]);
      if (DEPARTMENT_ONLY_HANDLES[key]) labelsByKey[key] = existing[i];
    }

    var selectedDepartments = effectiveDepartmentKeys(context);
    var brands = effectiveBrandLabels(context);
    var currentHandle = context.handle || "";
    var html = "";
    for (var g = 0; g < DEPARTMENT_OPTIONS.length; g++) {
      var departmentKey = DEPARTMENT_OPTIONS[g];
      var label = labelsByKey[departmentKey] || titleCaseLabel(departmentKey);
      var isSelected = selectedDepartments.indexOf(departmentKey) !== -1;
      var nextDepartments = selectedDepartments.slice();
      var departmentIndex = nextDepartments.indexOf(departmentKey);
      if (departmentIndex === -1) nextDepartments.push(departmentKey);
      else nextDepartments.splice(departmentIndex, 1);
      var dest = resolveDestination(context.typeKey, nextDepartments, brands);
      var href = buildUrlForDestination(dest, nextDepartments, brands, [PARAM_DEPARTMENT]);
      var useNotAFilter = dest.handle !== currentHandle;
      var count = countForDepartmentCheckbox(departmentKey, context);
      html += buildCheckboxItemHtml(label, href, isSelected, count, "", useNotAFilter);
    }

    list.innerHTML = html;
    list.classList.remove("is--style-radio");
    list.classList.add("is--style-checkbox");
    block.setAttribute(NAV_KIND_ATTR, "department");
    block.setAttribute(NAV_READY_ATTR, token);
  }

  /**
   * Brand lists must never rely on S&D filter.values (hard cap of 50; we have
   * hundreds of brands). Always build checkboxes from products.json. Destination
   * handle follows resolveDestination (brands as filter.* on type / type+department).
   */
  function contextForBrandOptionList(context) {
    if (context.typeKey) {
      return {
        handle: context.typeKey,
        mode: "type",
        typeKey: context.typeKey,
        departmentKey: null,
        brandSlug: "",
      };
    }
    return {
      handle: "all",
      mode: "all",
      typeKey: null,
      departmentKey: null,
      brandSlug: "",
    };
  }

  function extractBrandItemLabel(item) {
    var link = item.querySelector("a");
    if (!link) return "";
    var clone = link.cloneNode(true);
    var extras = clone.querySelectorAll(
      ".t4s-checkbox-wrapper, .t4s-radio-wrapper, .t4s-value-count, .t4s-shape-icon"
    );
    for (var i = 0; i < extras.length; i++) {
      extras[i].remove();
    }
    return (clone.textContent || "").replace(/\s+/g, " ").trim();
  }

  function findBrandListItem(list, brandLabel) {
    var target = handleize(brandLabel);
    var items = list.querySelectorAll("li");
    for (var i = 0; i < items.length; i++) {
      if (handleize(extractBrandItemLabel(items[i])) === target) return items[i];
    }
    return null;
  }

  function buildSearchAddingParamValue(paramName, valueToAdd) {
    var params = new URLSearchParams(window.location.search);
    var already = false;
    params.getAll(paramName).forEach(function (value) {
      if (decodeParamValue(value) === valueToAdd || value === valueToAdd) already = true;
    });
    if (!already) params.append(paramName, valueToAdd);
    var query = params.toString();
    return query ? "?" + query : "";
  }

  function selectedBrandLabelsFromUrl() {
    return new URLSearchParams(window.location.search)
      .getAll(PARAM_BRAND)
      .map(decodeParamValue)
      .filter(Boolean);
  }

  function isBrandLabelSelected(brandLabel, selectedLabels) {
    var target = String(brandLabel).toLowerCase();
    for (var i = 0; i < selectedLabels.length; i++) {
      if (String(selectedLabels[i]).toLowerCase() === target) return true;
      if (handleize(selectedLabels[i]) === handleize(brandLabel)) return true;
    }
    return false;
  }

  function collectCatalogBrandOptions(context) {
    var departments = effectiveDepartmentKeys(context);
    var refine = readRefineFromUrl([PARAM_BRAND, PARAM_DEPARTMENT]);
    var counts = {};
    for (var i = 0; i < catalogState.products.length; i++) {
      var product = catalogState.products[i];
      if (!product.b) continue;
      if (context.typeKey && product.y !== TYPE_CODE_BY_KEY[context.typeKey]) continue;
      if (departments.length) {
        var departmentCodes = departments
          .map(function (key) {
            return DEPARTMENT_CODE_BY_KEY[key];
          })
          .filter(Boolean);
        var productDepartments = product.dp || [];
        var departmentHit = departmentCodes.some(function (code) {
          return productDepartments.indexOf(code) !== -1;
        });
        if (!departmentHit) continue;
      }
      if (!productMatchesRefine(product, refine.selected, refine.priceGte, refine.priceLte)) {
        continue;
      }
      var brandName = String(product.b);
      counts[brandName] = (counts[brandName] || 0) + 1;
    }

    var selectedLabels = effectiveBrandLabels(context);
    selectedLabels.forEach(function (brandLabel) {
      var matchedKey = null;
      Object.keys(counts).forEach(function (key) {
        if (
          key.toLowerCase() === brandLabel.toLowerCase() ||
          handleize(key) === handleize(brandLabel)
        ) {
          matchedKey = key;
        }
      });
      if (!matchedKey) counts[brandLabel] = 0;
    });

    return Object.keys(counts)
      .sort(function (a, b) {
        return a.localeCompare(b, undefined, { sensitivity: "base" });
      })
      .map(function (brandName) {
        return {
          label: brandName,
          count: counts[brandName],
          selected: isBrandLabelSelected(brandName, selectedLabels),
        };
      });
  }

  function buildBrandCheckboxItemHtml(brandLabel, count, isSelected, context) {
    var departments = effectiveDepartmentKeys(context);
    var brands = effectiveBrandLabels(context).slice();
    var found = -1;
    for (var i = 0; i < brands.length; i++) {
      if (String(brands[i]).toLowerCase() === String(brandLabel).toLowerCase()) {
        found = i;
        break;
      }
    }
    if (isSelected && found !== -1) brands.splice(found, 1);
    else if (!isSelected && found === -1) brands.push(brandLabel);

    var dest = resolveDestination(context.typeKey, departments, brands);
    var href = buildUrlForDestination(dest, departments, brands, [PARAM_BRAND]);
    var useNotAFilter = dest.handle !== (context.handle || "");
    var isDisabled = !isSelected && Number(count) === 0;
    var liClasses = [];
    if (isSelected) liClasses.push("is--selected");
    if (isDisabled) liClasses.push("is--disabled");
    var liClass = liClasses.length ? ' class="' + liClasses.join(" ") + '"' : "";
    var countHtml =
      count === null || count === undefined
        ? ""
        : '<span class="t4s-value-count">(' + count + ")</span>";
    var notAFilter = useNotAFilter ? " data-not-a-filter" : "";
    return (
      "<li" +
      liClass +
      ' data-native-catalog-brand="1">' +
      '<a href="' +
      escapeAttr(href) +
      '"' +
      notAFilter +
      ">" +
      '<div class="t4s-checkbox-wrapper t4s-pr t4s-oh">' +
      CHECK_SVG +
      "</div>" +
      escapeHtml(brandLabel) +
      countHtml +
      "</a></li>"
    );
  }

  function insertBrandItemAlphabetically(list, brandLabel, html) {
    var brandLower = String(brandLabel).toLowerCase();
    var items = list.querySelectorAll("li");
    for (var i = 0; i < items.length; i++) {
      var existing = extractBrandItemLabel(items[i]);
      if (!existing) continue;
      if (existing.toLowerCase() > brandLower) {
        items[i].insertAdjacentHTML("beforebegin", html);
        return;
      }
    }
    list.insertAdjacentHTML("beforeend", html);
  }

  /** Fallback while products.json is loading: only ensure URL-selected brands exist. */
  function ensureUrlBrandsInCheckboxList(block, context) {
    var list = getOrCreateList(block);
    if (!list) return;

    var brands = selectedBrandLabelsFromUrl();
    if (!brands.length && context.brandSlug) {
      brands = [brandLabelFromSlug(context.brandSlug)];
    }
    if (!brands.length) return;

    for (var i = 0; i < brands.length; i++) {
      var brandLabel = brands[i];
      var existing = findBrandListItem(list, brandLabel);
      if (existing) {
        existing.classList.add("is--selected");
        existing.classList.remove("is--disabled");
        continue;
      }
      insertBrandItemAlphabetically(
        list,
        brandLabel,
        buildBrandCheckboxItemHtml(brandLabel, null, true, context)
      );
    }
  }

  function enhanceBrandCheckboxFromCatalog(block, context) {
    var list = getOrCreateList(block);

    if (!Array.isArray(catalogState.products) || !catalogState.products.length) {
      ensureUrlBrandsInCheckboxList(block, context);
      return;
    }

    var token = navToken("brand-checkbox", context);
    if (block.getAttribute(URL_BRANDS_ATTR) === token) return;

    var options = collectCatalogBrandOptions(context);
    if (!options.length) {
      ensureUrlBrandsInCheckboxList(block, context);
      block.setAttribute(URL_BRANDS_ATTR, token);
      return;
    }

    var html = "";
    for (var i = 0; i < options.length; i++) {
      html += buildBrandCheckboxItemHtml(
        options[i].label,
        options[i].count,
        options[i].selected,
        context
      );
    }
    list.innerHTML = html;
    list.classList.remove("is--style-radio");
    list.classList.add("is--style-checkbox");
    block.setAttribute(NAV_KIND_ATTR, "brand");
    block.setAttribute(URL_BRANDS_ATTR, token);
  }

  function enhanceBrandBlock(block, context) {
    enhanceBrandCheckboxFromCatalog(block, context);
  }

  function shouldShowTypeFacet(context) {
    return !!(context && context.mode === "all");
  }

  function shouldShowDepartmentFacet(context) {
    /* Hide when department is already encoded in the path (before ?). */
    return !(context && context.departmentKey);
  }

  function removeTypeFacetBlocks() {
    findFacetBlocks("type").forEach(function (block) {
      if (block && block.parentNode) block.parentNode.removeChild(block);
    });
  }

  function removeDepartmentFacetBlocks() {
    findFacetBlocks("department").forEach(function (block) {
      if (block && block.parentNode) block.parentNode.removeChild(block);
    });
  }

  function shouldInjectNavFacets(context) {
    return (
      context.mode === "all" ||
      context.mode === "type" ||
      context.mode === "type_department" ||
      context.mode === "department" ||
      context.mode === "brand" ||
      context.mode === "brand_type" ||
      !!context.typeKey
    );
  }

  function ensureNavBlocksInForm(form, context) {
    if (!form || !shouldInjectNavFacets(context)) return;

    var showType = shouldShowTypeFacet(context);
    var showDepartment = shouldShowDepartmentFacet(context);
    var typeBlock = null;
    var departmentBlock = null;
    var facets = form.querySelectorAll(".t4s-facet");
    for (var i = 0; i < facets.length; i++) {
      var facet = facets[i];
      if (facet.hasAttribute("data-vl-facet-block")) continue;
      if (facet.classList.contains("is--blockideyewear-type")) typeBlock = facet;
      if (
        facet.classList.contains("is--blockiddepartment") ||
        facet.classList.contains("is--blockidgender")
      ) {
        departmentBlock = facet;
      }
      var titleEl = null;
      for (var c = 0; c < facet.children.length; c++) {
        if (facet.children[c].classList.contains("t4s-facet-title")) {
          titleEl = facet.children[c];
          break;
        }
      }
      if (!titleEl) continue;
      var label = (titleEl.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (label === "eyewear type") typeBlock = facet;
      if (label === "department" || label === "departments" || label === "gender") {
        departmentBlock = facet;
      }
    }

    if (!showType) {
      if (typeBlock && typeBlock.parentNode) typeBlock.parentNode.removeChild(typeBlock);
      typeBlock = null;
    } else if (!typeBlock) {
      typeBlock = document.createElement("div");
      typeBlock.className = "t4s-col-item t4s-col-12 t4s-facet is--blockideyewear-type";
      typeBlock.innerHTML =
        '<h5 class="t4s-facet-title">Eyewear Type</h5><div class="t4s-facet-content"><ul class="t4s-filter__values is--style-checkbox t4s-current-scrollbar"></ul></div>';
      form.insertBefore(typeBlock, form.firstChild);
    }

    if (!showDepartment) {
      if (departmentBlock && departmentBlock.parentNode) {
        departmentBlock.parentNode.removeChild(departmentBlock);
      }
      departmentBlock = null;
    } else if (!departmentBlock) {
      departmentBlock = document.createElement("div");
      departmentBlock.className = "t4s-col-item t4s-col-12 t4s-facet is--blockiddepartment";
      departmentBlock.innerHTML =
        '<h5 class="t4s-facet-title">Department</h5><div class="t4s-facet-content"><ul class="t4s-filter__values is--style-checkbox t4s-current-scrollbar"></ul></div>';
      if (typeBlock && typeBlock.parentNode === form) {
        typeBlock.insertAdjacentElement("afterend", departmentBlock);
      } else {
        form.insertBefore(departmentBlock, form.firstChild);
      }
    }
  }

  function ensureNavBlocks(context) {
    if (!shouldInjectNavFacets(context)) return;

    var forms = findNativeFilterForms();
    if (!forms.length) {
      var fallback =
        document.querySelector("#FacetFiltersForm:not([data-vl-collection-filter])") ||
        document.querySelector("[data-replace-filter] #FacetFiltersForm");
      if (fallback) forms = [fallback];
    }

    for (var i = 0; i < forms.length; i++) {
      ensureNavBlocksInForm(forms[i], context);
    }

    /*
     * Empty [data-replace-filter] hosts (Sidebar drawer before facets AJAX)
     * get a minimal form so Department (and Type on All) can still appear.
     */
    var emptyHosts = document.querySelectorAll("[data-replace-filter]");
    for (var h = 0; h < emptyHosts.length; h++) {
      var host = emptyHosts[h];
      if (host.querySelector("#FacetFiltersForm")) continue;
      if (host.closest("[data-vl-collection-filter]")) continue;
      var form = document.createElement("form");
      form.id = "FacetFiltersForm";
      form.className = "t4s-facets__form t4s-row t4s-g-0";
      form.setAttribute("data-sidebar-links", "");
      host.appendChild(form);
      ensureNavBlocksInForm(form, context);
    }
  }

  function clearNativePriceApplyTimer() {
    if (priceApplyTimer) {
      clearTimeout(priceApplyTimer);
      priceApplyTimer = null;
    }
  }

  function readPriceInputNumber(input) {
    if (!input) return NaN;
    return Number(String(input.value).replace(/,/g, ""));
  }

  function pricesApproximatelyEqual(left, right) {
    if (isNaN(left) && isNaN(right)) return true;
    if (isNaN(left) || isNaN(right)) return false;
    return Math.abs(left - right) < 0.005;
  }

  /**
   * Hide theme Apply (CSS) and debounce-apply price via the hidden button so
   * facets.js loadPageContent / URL update stay unchanged.
   */
  function bindPriceAutoApplyOnWrapper(wrapper) {
    if (!wrapper || wrapper.getAttribute(PRICE_AUTO_ATTR) === "1") return;
    if (wrapper.closest("[data-vl-collection-filter]")) return;

    var stepsSlider = wrapper.querySelector(".t4s-price_steps_slider.noUi-target");
    if (!stepsSlider || !stepsSlider.noUiSlider) return;

    var minInput = wrapper.querySelector(".t4s-min_price");
    var maxInput = wrapper.querySelector(".t4s-max_price");
    var applyBtn = wrapper.querySelector(".t4s-price_slider_btn");
    if (!minInput || !maxInput || !applyBtn) return;

    wrapper.setAttribute(PRICE_AUTO_ATTR, "1");

    var lastAppliedMin = readPriceInputNumber(minInput);
    var lastAppliedMax = readPriceInputNumber(maxInput);
    var userMovedPrice = false;

    stepsSlider.noUiSlider.on("start", function () {
      userMovedPrice = true;
    });

    stepsSlider.noUiSlider.on("update", function () {
      if (!userMovedPrice) return;
      clearNativePriceApplyTimer();
      priceApplyTimer = setTimeout(function () {
        priceApplyTimer = null;
        if (isCustomFilterActive()) return;
        var currentMin = readPriceInputNumber(minInput);
        var currentMax = readPriceInputNumber(maxInput);
        if (
          pricesApproximatelyEqual(currentMin, lastAppliedMin) &&
          pricesApproximatelyEqual(currentMax, lastAppliedMax)
        ) {
          return;
        }
        lastAppliedMin = currentMin;
        lastAppliedMax = currentMax;
        userMovedPrice = false;
        applyBtn.click();
      }, PRICE_APPLY_DELAY_MS);
    });
  }

  function bindPriceAutoApply() {
    if (isCustomFilterActive()) return;
    var wrappers = document.querySelectorAll(".t4s-price_slider_wrapper");
    for (var i = 0; i < wrappers.length; i++) {
      bindPriceAutoApplyOnWrapper(wrappers[i]);
    }
  }

  function enhanceNavFacets() {
    if (isCustomFilterActive()) return;
    if (isEnhancingNav) {
      scheduleEnhanceNavFacets(80);
      return;
    }

    isEnhancingNav = true;
    if (filterHostObserver) filterHostObserver.disconnect();
    try {
      ensureStyles();
      var context = parseCollectionContext(collectionHandleFromPath());
      ensureNavBlocks(context);

      if (shouldShowTypeFacet(context)) {
        var typeBlocks = findFacetBlocks("type");
        for (var t = 0; t < typeBlocks.length; t++) {
          enhanceTypeBlock(typeBlocks[t], context);
        }
      } else {
        removeTypeFacetBlocks();
      }

      if (shouldShowDepartmentFacet(context)) {
        var departmentBlocks = findFacetBlocks("department");
        for (var g = 0; g < departmentBlocks.length; g++) {
          enhanceDepartmentBlock(departmentBlocks[g], context);
        }
      } else {
        removeDepartmentFacetBlocks();
      }

      var brandBlocks = findFacetBlocks("brand");
      for (var b = 0; b < brandBlocks.length; b++) {
        enhanceBrandBlock(brandBlocks[b], context);
      }

      enhanceActiveFilterContextChips();
      bindPriceAutoApply();
    } finally {
      isEnhancingNav = false;
      if (filterHostObserver) {
        filterHostObserver.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  /**
   * True when any native Type/Department/Brand block still looks like raw S&D
   * (theme AJAX just replaced Sidebar HTML).
   */
  function hasUnenhancedNavBlocks() {
    if (isCustomFilterActive()) return false;
    var context = parseCollectionContext(collectionHandleFromPath());
    if (!shouldInjectNavFacets(context)) return false;

    var needsType = shouldShowTypeFacet(context);
    var needsDepartment = shouldShowDepartmentFacet(context);
    var typeBlocks = findFacetBlocks("type");
    var departmentBlocks = findFacetBlocks("department");
    var tokenType = navToken("type", context);
    var tokenDepartment = navToken("department-checkbox", context);
    var i;

    if ((needsType && !typeBlocks.length) || (needsDepartment && !departmentBlocks.length)) {
      /* Forms exist but blocks missing — still need ensureNavBlocks. */
      return findNativeFilterForms().length > 0 || !!document.querySelector("[data-replace-filter]");
    }

    if (needsType) {
      for (i = 0; i < typeBlocks.length; i++) {
        var typeList = typeBlocks[i].querySelector("ul.t4s-filter__values");
        if (
          typeBlocks[i].getAttribute(NAV_READY_ATTR) !== tokenType ||
          !typeList ||
          !typeList.classList.contains("is--style-radio") ||
          typeList.classList.contains("is--style-checkbox") ||
          typeList.querySelectorAll("li").length < TYPE_OPTIONS.length ||
          !typeList.querySelector(".t4s-radio-wrapper")
        ) {
          return true;
        }
      }
    }

    if (needsDepartment) {
      for (i = 0; i < departmentBlocks.length; i++) {
        var departmentList = departmentBlocks[i].querySelector("ul.t4s-filter__values");
        if (
          departmentBlocks[i].getAttribute(NAV_READY_ATTR) !== tokenDepartment ||
          !departmentList ||
          !departmentList.classList.contains("is--style-checkbox") ||
          departmentList.classList.contains("is--style-radio") ||
          departmentList.querySelectorAll("li").length < DEPARTMENT_OPTIONS.length ||
          !departmentList.querySelector(".t4s-checkbox-wrapper")
        ) {
          return true;
        }
      }
    } else if (departmentBlocks.length) {
      /* Path already has department — leftover S&D block must be removed. */
      return true;
    }

    /* Brand checkboxes from catalog on every mode that shows Brand. */
    var brandBlocks = findFacetBlocks("brand");
    if (brandBlocks.length) {
      var catalogReady =
        Array.isArray(catalogState.products) && catalogState.products.length > 0;
      var tokenCheckbox = navToken("brand-checkbox", context);
      for (i = 0; i < brandBlocks.length; i++) {
        if (catalogReady) {
          if (brandBlocks[i].getAttribute(URL_BRANDS_ATTR) !== tokenCheckbox) {
            return true;
          }
          if (!brandBlocks[i].querySelector(".t4s-checkbox-wrapper")) return true;
        } else {
          var urlBrands = selectedBrandLabelsFromUrl();
          if (!urlBrands.length && context.brandSlug) {
            urlBrands = [brandLabelFromSlug(context.brandSlug)];
          }
          if (!urlBrands.length) {
            if (
              brandBlocks[i].querySelector(
                ".native-brands-loading, .clearance-brands-loading"
              )
            ) {
              return true;
            }
            continue;
          }
          var brandList = brandBlocks[i].querySelector("ul.t4s-filter__values");
          if (!brandList) return true;
          for (var u = 0; u < urlBrands.length; u++) {
            if (!findBrandListItem(brandList, urlBrands[u])) return true;
          }
        }
      }
    }

    if (hasUnboundPriceSliders()) return true;

    return false;
  }

  function hasUnboundPriceSliders() {
    var sliders = document.querySelectorAll(
      ".t4s-price_slider_wrapper .t4s-price_steps_slider.noUi-target"
    );
    for (var i = 0; i < sliders.length; i++) {
      var wrapper = sliders[i].closest(".t4s-price_slider_wrapper");
      if (!wrapper) continue;
      if (wrapper.closest("[data-vl-collection-filter]")) continue;
      if (wrapper.getAttribute(PRICE_AUTO_ATTR) !== "1") return true;
    }
    return false;
  }

  /**
   * Debounce: theme facets.js replaces #t4s-filter-hidden and [data-replace-filter]
   * in one AJAX callback — run after that stack (and a short follow-up for late writes).
   */
  function scheduleEnhanceNavFacets(delayMs) {
    if (isCustomFilterActive()) return;
    var delay = typeof delayMs === "number" ? delayMs : 50;
    if (enhanceNavTimer) {
      clearTimeout(enhanceNavTimer);
      enhanceNavTimer = null;
    }
    enhanceNavTimer = setTimeout(function () {
      enhanceNavTimer = null;
      enhanceNavFacets();
      if (!hasUnenhancedNavBlocks()) return;
      if (enhanceNavRetryTimer) clearTimeout(enhanceNavRetryTimer);
      enhanceNavRetryTimer = setTimeout(function () {
        enhanceNavRetryTimer = null;
        enhanceNavFacets();
      }, 120);
    }, delay);
  }

  /**
   * After a refine-filter click, theme AJAX rewrites Sidebar later (nested fetch).
   * Several passes — do not coalesce into one timeout.
   */
  function resyncNavAfterThemeAjax() {
    if (isCustomFilterActive()) return;
    var delays = [80, 250, 600, 1200];
    for (var i = 0; i < delays.length; i++) {
      (function (ms) {
        setTimeout(function () {
          if (isCustomFilterActive()) return;
          enhanceNavFacets();
        }, ms);
      })(delays[i]);
    }
  }

  function onFacetsUpdated() {
    scheduleEnhanceNavFacets(50);
  }

  function bindFacetsLoaded() {
    function attach($) {
      $(document).on("facets:loaded sidebar:updated sidebar:facets", onFacetsUpdated);
    }

    if (typeof window.jQuery_T4NT === "function") {
      attach(window.jQuery_T4NT);
      return;
    }
    if (typeof window.jQuery === "function") {
      attach(window.jQuery);
    }
  }

  /**
   * Theme facets.js AJAX-replaces filter HTML after refine clicks (no facets:loaded).
   * Schedule several passes so Sidebar is re-enhanced after that write.
   */
  function bindRefineFilterClickResync() {
    if (document.documentElement.getAttribute("data-native-nav-click-resync") === "1") {
      return;
    }
    document.documentElement.setAttribute("data-native-nav-click-resync", "1");

    document.addEventListener(
      "click",
      function (event) {
        if (isCustomFilterActive()) return;
        var link = event.target.closest(
          "[data-sidebar-links] a, [data-filter-links] a, .t4s-active-filters a, .t4s-filter__values a"
        );
        if (!link) return;
        if (link.hasAttribute("data-not-a-filter")) return;
        if (link.getAttribute("data-vl-remove-context") || link.getAttribute("data-vl-facet")) {
          return;
        }
        resyncNavAfterThemeAjax();
      },
      true
    );

    window.addEventListener("popstate", function () {
      if (isCustomFilterActive()) return;
      resyncNavAfterThemeAjax();
    });
  }

  function observeFilterHosts() {
    if (typeof MutationObserver === "undefined") return;

    if (filterHostObserver) {
      filterHostObserver.disconnect();
      filterHostObserver = null;
    }

    filterHostObserver = new MutationObserver(function () {
      if (isCustomFilterActive() || isEnhancingNav) return;
      scheduleEnhanceNavFacets(50);
    });

    /*
     * Always watch the document: Sidebar drawer (#drawer-*) is injected after
     * first paint, and theme AJAX rewrites filter HTML without facets:loaded.
     * Narrow roots alone miss those late hosts.
     */
    filterHostObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function startNavEnhanceWatchdog() {
    if (document.documentElement.getAttribute("data-native-nav-watchdog") === "1") {
      return;
    }
    document.documentElement.setAttribute("data-native-nav-watchdog", "1");
    var checksLeft = 120;
    var watchdogTimer = setInterval(function () {
      if (isCustomFilterActive()) {
        clearInterval(watchdogTimer);
        return;
      }
      checksLeft -= 1;
      if (checksLeft <= 0) {
        clearInterval(watchdogTimer);
        return;
      }
      bindPriceAutoApply();
      if (hasUnenhancedNavBlocks()) {
        scheduleEnhanceNavFacets(0);
      }
    }, 500);
  }

  function pollUntilPresent() {
    var attempts = 0;
    var maxAttempts = 40;
    var timer = setInterval(function () {
      attempts += 1;
      scheduleEnhanceNavFacets(0);
      var found =
        findFacetBlocks("type").length > 0 || findFacetBlocks("department").length > 0;
      if ((found && !hasUnenhancedNavBlocks()) || attempts >= maxAttempts || isCustomFilterActive()) {
        clearInterval(timer);
      }
    }, 250);
  }

  function init() {
    if (isCustomFilterActive()) return;

    ensureStyles();
    bindContextChipNavigation();
    scheduleEnhanceNavFacets(0);
    bindFacetsLoaded();
    bindRefineFilterClickResync();
    observeFilterHosts();
    pollUntilPresent();
    startNavEnhanceWatchdog();
    ensureCatalogLoaded().then(function () {
      if (isCustomFilterActive()) return;
      scheduleEnhanceNavFacets(0);
    });
  }

  if (!isCustomFilterActive()) {
    ensureStyles();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
